/**
 * `useMpxAgGridState` — server-persisted AG-Grid view state via mpx.
 *
 * Replaces the legacy `useAgGridState` hook (browser-storage + AG-Grid
 * legacy Column State API) with a hook backed by the centro-portal BFF
 * grid-states endpoints (XTRM-1829, mpx contract from XTRM-1760) and
 * AG-Grid's modern Grid State API.
 *
 * The legacy hook is intentionally left in place; call sites are
 * migrated per-site under XTRM-1831, after which it is deleted.
 *
 * Usage:
 *
 * ```tsx
 * const { gridProps, reset, isLoading } = useMpxAgGridState({
 *   scopeType: "rfp",
 *   scopeId: rfpId,
 *   gridId: "rfp-responses",
 * });
 *
 * if (isLoading) return <Spinner />;
 *
 * return <AgGridReact {...gridProps} {...otherProps} />;
 * ```
 *
 * - `gridProps.initialState` is set from the server payload, or
 *   `undefined` if nothing has been persisted yet (404 → fresh grid).
 * - `gridProps.onStateUpdated` debounces a PUT of `api.getState()`.
 * - `gridProps.key` bumps on `reset()` so AG-Grid drops in-memory
 *   state and re-mounts cleanly.
 *
 * Reference: src/apps/backend/mpx/README.md
 */

import { useApi } from "@platform/api";
import { logger } from "@platform/browser-logger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  deleteGridState,
  extractAgGridState,
  extractRowOrder,
  getGridState,
  type AgGridStatePayload,
  type GridStateResponse,
  type GridStateScope,
  type GridStateScopeType,
  type RowOrderPayload,
} from "./gridStateClient";

import type { GridState, StateUpdatedEvent } from "ag-grid-community";

/** Default trailing-edge debounce for PUTs on `onStateUpdated`. */
export const DEFAULT_DEBOUNCE_MS = 500;

/**
 * Configuration for `useMpxAgGridState`. `scopeId` is required for all
 * scopes other than `global` and is forbidden for `global` — the
 * discriminated union encodes both rules at the type level.
 */
export type UseMpxAgGridStateConfig =
  | {
      scopeType: "global";
      scopeId?: undefined;
      gridId: string;
      /** Trailing-edge debounce for PUTs on state updates. */
      debounceMs?: number;
      /**
       * When false, the hook does not GET on mount and does not PUT on
       * state updates. Use this while waiting for an async-resolved
       * `scopeId` (e.g., a route param). Defaults to true.
       */
      enabled?: boolean;
    }
  | {
      scopeType: Exclude<GridStateScopeType, "global">;
      scopeId: string;
      gridId: string;
      debounceMs?: number;
      enabled?: boolean;
    };

/**
 * Props for `<AgGridReact>` produced by the hook. Spread this onto the
 * grid; combine with the rest of your grid props as usual.
 */
export interface MpxAgGridStateProps {
  /** Bumps on `reset()` to force AG-Grid to drop its in-memory state. */
  key: number;
  /** Server-persisted state, or `undefined` when no state is saved. */
  initialState: GridState | undefined;
  /** Debounced PUT-on-change handler. */
  onStateUpdated: (event: StateUpdatedEvent) => void;
}

export interface UseMpxAgGridStateResult {
  /** Spread onto `<AgGridReact>`. */
  gridProps: MpxAgGridStateProps;
  /** Whether the initial GET is still in flight. */
  isLoading: boolean;
  /** Set if the initial GET surfaced a non-404 error. */
  error: unknown;
  /**
   * Persisted user-defined row order, when one has been saved. Keyed by
   * the same row IDs the grid emits via `getRowId` / `node.id`. Callers
   * that opt into row ordering reorder their `rowData` against this list
   * before passing it to AG-Grid.
   */
  rowOrder: string[] | undefined;
  /**
   * Persists a new row order. Triggers a debounced PUT alongside any
   * pending grid-state changes. Pass an empty array to clear.
   */
  setRowOrder: (ids: string[]) => void;
  /**
   * DELETE the persisted state and remount the grid with no
   * `initialState`. Returns when the server has acknowledged the
   * delete; the remount happens synchronously immediately after.
   */
  reset: () => Promise<void>;
}

/**
 * React Query key factory. Exported so call sites and tests can
 * invalidate or seed the cache without re-deriving the shape.
 */
export const mpxGridStateQueryKey = (
  scopeType: GridStateScopeType,
  scopeId: string | undefined,
  gridId: string,
): readonly ["mpx", "grid-states", GridStateScopeType, string, string] => [
  "mpx",
  "grid-states",
  scopeType,
  scopeId ?? "",
  gridId,
];

export function useMpxAgGridState(
  config: UseMpxAgGridStateConfig,
): UseMpxAgGridStateResult {
  const {
    scopeType,
    scopeId,
    gridId,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    enabled = true,
  } = config;
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  const scope = useMemo<GridStateScope>(
    () =>
      scopeType === "global"
        ? { type: "global" }
        : { type: scopeType, id: scopeId as string },
    [scopeType, scopeId],
  );
  const queryKey = useMemo(
    () => mpxGridStateQueryKey(scopeType, scopeId, gridId),
    [scopeType, scopeId, gridId],
  );

  const query = useQuery<GridStateResponse | null>({
    queryKey,
    queryFn: () => getGridState(scope, gridId, axiosInstance),
    // The client already converts 404 to `null`; everything that
    // reaches React Query as an error is a real failure (5xx, network,
    // etc.). One retry is enough to absorb a transient blip without
    // making the user wait through a long backoff for a real outage.
    retry: 1,
    enabled,
  });

  const resetMutation = useMutation({
    mutationFn: () => deleteGridState(scope, gridId, axiosInstance),
    onSuccess: () => {
      // Drop the cached state so the next mount sees "no saved state".
      queryClient.setQueryData<GridStateResponse | null>(queryKey, null);
    },
    onError: (mutationError) => {
      logger.error(
        "[useMpxAgGridState] Failed to reset grid state",
        mutationError as Error,
        { scopeType, scopeId: scopeId ?? null, gridId },
      );
    },
  });

  // Surface GET failures (5xx, network) to Datadog. 404s are converted
  // to `null` in the client and never reach here.
  useEffect(() => {
    if (query.error) {
      logger.error(
        "[useMpxAgGridState] Failed to load grid state",
        query.error as Error,
        { scopeType, scopeId: scopeId ?? null, gridId },
      );
    }
  }, [query.error, scopeType, scopeId, gridId]);

  // Both fields must be PUT together (the meta envelope is one row).
  // Hold the most recent value of each in refs so a debounced flush can
  // include the latest of both, even when only one changed.
  const persistedAgGridState = extractAgGridState(query.data);
  const persistedRowOrder = extractRowOrder(query.data);
  const latestAgGridRef = useRef<AgGridStatePayload | undefined>(
    persistedAgGridState,
  );
  const latestRowOrderRef = useRef<RowOrderPayload | undefined>(
    persistedRowOrder,
  );

  // Once the user has made a local edit, the refs are the source of
  // truth until the (scope, gridId) tuple changes — passive cache
  // syncs (e.g. a window-focus refetch resolving while a debounced
  // PUT is still in flight) must not clobber unflushed work.
  const dirtyRef = useRef(false);

  // Reset hydration/dirty tracking whenever the scope or grid changes
  // so the new tuple's persisted state hydrates the refs cleanly.
  useEffect(() => {
    dirtyRef.current = false;
    latestAgGridRef.current = undefined;
    latestRowOrderRef.current = undefined;
  }, [scopeType, scopeId, gridId]);

  // Passive sync from the React Query cache into the refs. Skip while
  // dirty: the local refs already have the user's latest input and the
  // pending debounced flush will reconcile with the server.
  useEffect(() => {
    if (dirtyRef.current) {
      return;
    }
    latestAgGridRef.current = persistedAgGridState;
    latestRowOrderRef.current = persistedRowOrder;
  }, [persistedAgGridState, persistedRowOrder]);

  // TODO: persistence is currently disabled — this debounced flush no
  // longer PUTs to the server (see commit message). It only clears
  // dirtyRef so passive cache syncs aren't permanently blocked.
  const debouncedFlush = useMemo(
    () =>
      debounce(() => {
        const agGrid = latestAgGridRef.current;
        if (!agGrid) {
          // Nothing to write yet (e.g. setRowOrder fired before the
          // grid produced its first state). The next state update will
          // carry the row order along.
          return;
        }

        dirtyRef.current = false;
      }, debounceMs),
    [debounceMs],
  );

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      if (!enabled || event.sources?.includes("gridInitializing")) {
        return;
      }
      const state = event.api.getState();
      // The server requires `version`. AG-Grid always populates it on
      // `getState()`; guard anyway so a malformed event can't corrupt
      // the row.
      const version = (state as { version?: string }).version;
      if (typeof version !== "string" || version === "") {
        return;
      }
      latestAgGridRef.current = state as unknown as AgGridStatePayload;
      dirtyRef.current = true;
      debouncedFlush();
    },
    [debouncedFlush, enabled],
  );

  const setRowOrder = useCallback(
    (ids: string[]) => {
      if (!enabled) {
        return;
      }
      latestRowOrderRef.current = { ids };
      dirtyRef.current = true;
      debouncedFlush();
    },
    [debouncedFlush, enabled],
  );

  const [resetKey, setResetKey] = useState(0);

  const reset = useCallback(async (): Promise<void> => {
    // Drop any pending PUT — we're about to delete and remount, so
    // those keystrokes shouldn't be persisted.
    debouncedFlush.cancel();
    latestAgGridRef.current = undefined;
    latestRowOrderRef.current = undefined;
    dirtyRef.current = false;
    await resetMutation.mutateAsync();
    setResetKey((k) => k + 1);
  }, [debouncedFlush, resetMutation]);

  const gridProps: MpxAgGridStateProps = {
    key: resetKey,
    initialState: (persistedAgGridState as GridState | undefined) ?? undefined,
    onStateUpdated,
  };

  return {
    gridProps,
    isLoading: query.isLoading,
    error: query.error,
    rowOrder: persistedRowOrder?.ids,
    setRowOrder,
    reset,
  };
}
